"use client";

import { useMemo, useState } from "react";
import { Field, Stat, fmtMoney } from "../ToolUi";

export default function CalculatorClient() {
  const [monthlyOrders, setMonthlyOrders] = useState("1000");
  const [aov, setAov] = useState("60");
  const [repeatRate, setRepeatRate] = useState("25");
  const [uplift, setUplift] = useState("15");
  const [programCost, setProgramCost] = useState("69");

  const results = useMemo(() => {
    const orders = Math.max(0, parseFloat(monthlyOrders) || 0);
    const avg = Math.max(0, parseFloat(aov) || 0);
    const repeat = Math.min(100, Math.max(0, parseFloat(repeatRate) || 0)) / 100;
    const lift = Math.max(0, parseFloat(uplift) || 0) / 100;
    const cost = Math.max(0, parseFloat(programCost) || 0);

    const monthlyRevenue = orders * avg;
    const repeatRevenue = monthlyRevenue * repeat;
    // Loyalty programs lift repeat behaviour: more repeat orders and a
    // modestly higher AOV from point-redemption psychology.
    const incrementalMonthly = repeatRevenue * lift;
    const incrementalYearly = incrementalMonthly * 12;
    const roi = cost > 0 ? incrementalMonthly / cost : null;
    const paybackDays =
      incrementalMonthly > 0 ? Math.ceil((cost / incrementalMonthly) * 30) : null;

    return { monthlyRevenue, repeatRevenue, incrementalMonthly, incrementalYearly, roi, paybackDays };
  }, [monthlyOrders, aov, repeatRate, uplift, programCost]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* Inputs */}
      <div className="flex flex-col gap-5 bg-[#1E1E1E] border-2 border-white/10 rounded-2xl p-6 md:p-8">
        <h2 className="font-normal text-[24px] text-[#f8f7f4] tracking-[-0.5px]">
          Your store numbers
        </h2>
        <Field label="Monthly orders" value={monthlyOrders} onChange={setMonthlyOrders} />
        <Field label="Average order value" suffix="USD" value={aov} onChange={setAov} step="0.01" />
        <Field
          label="Current repeat purchase rate"
          suffix="%"
          value={repeatRate}
          onChange={setRepeatRate}
          max={100}
        />
        <Field
          label="Expected repeat-revenue uplift from loyalty"
          suffix="%"
          value={uplift}
          onChange={setUplift}
          max={100}
        />
        <Field
          label="Monthly cost of your loyalty program"
          suffix="USD"
          value={programCost}
          onChange={setProgramCost}
          step="0.01"
        />
        <p className="text-[13px] text-[#909090] leading-5">
          Tip: merchants switching from discounts to points-based rewards
          typically model a 10–25% repeat-revenue uplift in year one.
        </p>
      </div>

      {/* Results */}
      <div className="flex flex-col gap-4">
        <Stat
          label="Extra revenue per month"
          value={fmtMoney(results.incrementalMonthly)}
          highlight
        />
        <Stat label="Extra revenue per year" value={fmtMoney(results.incrementalYearly)} />
        <div className="grid grid-cols-2 gap-4">
          <Stat
            label="ROI multiple"
            value={results.roi === null ? "∞" : `${results.roi.toFixed(1)}x`}
          />
          <Stat
            label="Payback period"
            value={
              results.paybackDays === null ? "—" : `${results.paybackDays} days`
            }
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Stat label="Current monthly revenue" value={fmtMoney(results.monthlyRevenue)} />
          <Stat label="Of which repeat revenue" value={fmtMoney(results.repeatRevenue)} />
        </div>

        <a
          href="https://apps.shopify.com/keystone-loyalty-rewards?utm_source=website&utm_medium=organic&utm_campaign=roi-calculator"
          className="mt-2 inline-flex items-center justify-center h-[52px] bg-[#dbdacd] border border-[#f8f7f4] rounded-[10px] px-6 text-base font-medium text-[#3e3e3e] hover:bg-[#cccbbe] transition-colors"
        >
          Start a 14-day free trial of Keystone Loyalty
        </a>
      </div>
    </div>
  );
}
